(() => {
  const STORAGE_KEY = 'ml_compare_products_v1';
  const MAX_PRODUCTS = 4;
  const INITIAL_SIZE_COUNT = 8;

  let selectedProducts = readStoredProducts();
  let previousFocus = null;
  let toastTimer = null;

  /* =========================================================
     HELPERS
     ========================================================= */

  function readStoredProducts() {
    try {
      const stored = JSON.parse(
        localStorage.getItem(STORAGE_KEY)
      );

      if (!Array.isArray(stored)) return [];

      return stored
        .filter((item) => item && item.id)
        .slice(0, MAX_PRODUCTS);
    } catch (error) {
      return [];
    }
  }

  function saveProducts() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(selectedProducts)
      );
    } catch (error) {
      // Compare still works during the current page session.
    }
  }

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function safeProductUrl(value = '') {
    try {
      const url = new URL(
        value,
        window.location.origin
      );

      if (
        url.origin !== window.location.origin
      ) {
        return '#';
      }

      return (
        url.pathname +
        url.search +
        url.hash
      );
    } catch (error) {
      return '#';
    }
  }

  function safeImageUrl(value = '') {
    if (!value) return '';

    try {
      const url = new URL(
        value,
        window.location.href
      );

      if (
        !['http:', 'https:'].includes(
          url.protocol
        )
      ) {
        return '';
      }

      return url.href;
    } catch (error) {
      return '';
    }
  }

  function formatSize(size = '') {
    return String(size)
      .replaceAll('"', '')
      .replace(/\s*[xX]\s*/g, ' × ')
      .trim();
  }

  function productFromButton(button) {
    const sizes = (
      button.dataset.productSizes || ''
    )
      .split('||')
      .map((size) => size.trim())
      .filter(Boolean);

    return {
      id: String(
        button.dataset.productId || ''
      ),

      title:
        button.dataset.productTitle || '',

      url:
        button.dataset.productUrl || '',

      image:
        button.dataset.productImage || '',

      price:
        button.dataset.productPrice || '',

      sizes
    };
  }

  function isSelected(productId) {
    return selectedProducts.some(
      (product) =>
        String(product.id) ===
        String(productId)
    );
  }


  /* =========================================================
     PRODUCT SELECTION
     ========================================================= */

  function addProduct(product) {
    if (
      !product.id ||
      isSelected(product.id)
    ) {
      return;
    }

    if (
      selectedProducts.length >=
      MAX_PRODUCTS
    ) {
      showToast(
        'You can compare up to 4 rugs at a time.'
      );

      return;
    }

    selectedProducts.push(product);

    saveProducts();
    renderAll();
  }

  function removeProduct(productId) {
    selectedProducts =
      selectedProducts.filter(
        (product) =>
          String(product.id) !==
          String(productId)
      );

    saveProducts();
    renderAll();

    if (
      selectedProducts.length < 2 &&
      overlay.classList.contains('is-open')
    ) {
      closeModal();
    }
  }

  function clearProducts() {
    selectedProducts = [];

    saveProducts();
    renderAll();

    if (
      imageViewer.classList.contains(
        'is-open'
      )
    ) {
      closeImageViewer();
    }

    if (
      overlay.classList.contains(
        'is-open'
      )
    ) {
      closeModal();
    }
  }

  function toggleProduct(button) {
    const product =
      productFromButton(button);

    if (isSelected(product.id)) {
      removeProduct(product.id);
    } else {
      addProduct(product);
    }
  }


  /* =========================================================
     FLOATING COMPARE TRAY
     ========================================================= */

  const tray =
    document.createElement('aside');

  tray.className =
    'ml-compare-tray';

  tray.setAttribute(
    'aria-label',
    'Rug comparison'
  );

  tray.innerHTML = `
    <div class="ml-compare-tray__inner">

      <div class="ml-compare-tray__heading">

        <span class="ml-compare-tray__title">
          Compare Rugs
        </span>

        <span class="ml-compare-tray__count">
          0 selected
        </span>

      </div>

      <div
        class="ml-compare-tray__items"
        data-ml-compare-tray-items
      ></div>

      <div class="ml-compare-tray__actions">

        <button
          type="button"
          class="ml-compare-tray__clear"
          data-ml-compare-clear
        >
          Clear
        </button>

        <button
          type="button"
          class="ml-compare-tray__open"
          data-ml-compare-open
          disabled
        >
          Choose 2 rugs
        </button>

      </div>

    </div>
  `;


  /* =========================================================
     COMPARE MODAL
     ========================================================= */

  const overlay =
    document.createElement('div');

  overlay.className =
    'ml-compare-overlay';

  overlay.hidden = true;

  overlay.innerHTML = `
    <div
      class="ml-compare-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="MLCompareHeading"
    >

      <div class="ml-compare-modal__header">

        <div class="ml-compare-modal__heading-wrap">

          <span class="ml-compare-modal__eyebrow">
            MONROE &amp; LEISURE
          </span>

          <h2
            id="MLCompareHeading"
            class="ml-compare-modal__heading"
          >
            Compare Rugs
          </h2>

        </div>

        <div class="ml-compare-modal__header-actions">

          <button
            type="button"
            class="ml-compare-modal__clear"
            data-ml-compare-clear
          >
            Clear All
          </button>

          <button
            type="button"
            class="ml-compare-modal__close"
            data-ml-compare-close
            aria-label="Close comparison"
          >
            ×
          </button>

        </div>

      </div>

      <div class="ml-compare-modal__body">

        <div
          class="ml-compare-grid"
          data-ml-compare-grid
        ></div>

      </div>

    </div>
  `;


  /* =========================================================
     FULL IMAGE VIEWER
     ========================================================= */

  const imageViewer =
    document.createElement('div');

  imageViewer.className =
    'ml-compare-image-viewer';

  imageViewer.hidden = true;

  imageViewer.innerHTML = `
    <div
      class="ml-compare-image-viewer__dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged rug image"
    >

      <button
        type="button"
        class="ml-compare-image-viewer__close"
        data-ml-image-viewer-close
        aria-label="Close enlarged image"
      >
        ×
      </button>

      <div class="ml-compare-image-viewer__image-area">

        <img
          src=""
          alt=""
          class="ml-compare-image-viewer__image"
          data-ml-image-viewer-image
        >

      </div>

      <div
        class="ml-compare-image-viewer__title"
        data-ml-image-viewer-title
      ></div>

    </div>
  `;


  /* =========================================================
     TOAST MESSAGE
     ========================================================= */

  const toast =
    document.createElement('div');

  toast.className =
    'ml-compare-toast';

  toast.setAttribute(
    'role',
    'status'
  );

  toast.setAttribute(
    'aria-live',
    'polite'
  );


  /* =========================================================
     COLLECTION CARD BUTTON STATES
     ========================================================= */

  function syncButtons() {
    document
      .querySelectorAll(
        '[data-ml-compare-button]'
      )
      .forEach((button) => {

        const selected =
          isSelected(
            button.dataset.productId
          );

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          selected
            ? 'true'
            : 'false'
        );

        const label =
          button.querySelector(
            '[data-ml-compare-label]'
          );

        if (label) {
          label.textContent =
            selected
              ? 'Added to Compare'
              : 'Compare';
        }
      });
  }


  /* =========================================================
     RENDER FLOATING TRAY
     ========================================================= */

  function renderTray() {
    const count =
      selectedProducts.length;

    tray.classList.toggle(
      'is-visible',
      count > 0
    );

    const countElement =
      tray.querySelector(
        '.ml-compare-tray__count'
      );

    if (countElement) {
      countElement.textContent =
        `${count} selected`;
    }

    const itemsElement =
      tray.querySelector(
        '[data-ml-compare-tray-items]'
      );

    if (itemsElement) {

      itemsElement.innerHTML =
        selectedProducts
          .map((product) => {

            const image =
              safeImageUrl(
                product.image
              );

            return `
              <div class="ml-compare-tray__item">

                ${
                  image
                    ? `
                      <div class="ml-compare-tray__item-image-wrap">

                        <img
                          src="${escapeHtml(image)}"
                          alt=""
                          class="ml-compare-tray__item-image"
                          loading="lazy"
                        >

                      </div>
                    `
                    : ''
                }

                <span class="ml-compare-tray__item-name">
                  ${escapeHtml(product.title)}
                </span>

                <button
                  type="button"
                  class="ml-compare-tray__remove"
                  data-ml-compare-remove="${escapeHtml(product.id)}"
                  aria-label="Remove ${escapeHtml(product.title)}"
                >
                  ×
                </button>

              </div>
            `;
          })
          .join('');
    }

    const openButton =
      tray.querySelector(
        '[data-ml-compare-open]'
      );

    if (!openButton) return;

    openButton.disabled =
      count < 2;

    if (count === 0) {

      openButton.textContent =
        'Choose 2 rugs';

    } else if (count === 1) {

      openButton.textContent =
        'Choose 1 more';

    } else {

      openButton.textContent =
        `Compare ${count} Rugs`;
    }
  }


  /* =========================================================
     PREMIUM SIZE TILES
     ========================================================= */

  function buildSizesMarkup(product) {
    const cleanSizes =
      Array.isArray(product.sizes)
        ? product.sizes.filter(
            (size) => {

              if (!size) {
                return false;
              }

              return !String(size)
                .toLowerCase()
                .includes('sample');
            }
          )
        : [];

    if (!cleanSizes.length) {
      return `
        <span class="ml-compare-no-sizes">
          See product for available sizes
        </span>
      `;
    }

    const visibleSizes =
      cleanSizes.slice(
        0,
        INITIAL_SIZE_COUNT
      );

    const hiddenSizes =
      cleanSizes.slice(
        INITIAL_SIZE_COUNT
      );

    const visibleMarkup =
      visibleSizes
        .map(
          (size) => `
            <span class="ml-compare-size">
              ${escapeHtml(formatSize(size))}
            </span>
          `
        )
        .join('');

    const hiddenMarkup =
      hiddenSizes
        .map(
          (size) => `
            <span
              class="ml-compare-size ml-compare-size--hidden"
              data-ml-extra-size
              hidden
            >
              ${escapeHtml(formatSize(size))}
            </span>
          `
        )
        .join('');

    const moreButton =
      hiddenSizes.length
        ? `
          <button
            type="button"
            class="ml-compare-size-more"
            data-ml-size-more
            data-more-count="${hiddenSizes.length}"
            aria-expanded="false"
          >
            + ${hiddenSizes.length} more sizes
          </button>
        `
        : '';

    return `
      <div class="ml-compare-size-list">

        ${visibleMarkup}

        ${hiddenMarkup}

      </div>

      ${moreButton}
    `;
  }


  /* =========================================================
     RENDER COMPARE CARDS
     ========================================================= */

  function renderModal() {
    const grid =
      overlay.querySelector(
        '[data-ml-compare-grid]'
      );

    if (!grid) return;

    grid.style.setProperty(
      '--ml-compare-columns',
      Math.max(
        selectedProducts.length,
        2
      )
    );

    grid.innerHTML =
      selectedProducts
        .map((product) => {

          const image =
            safeImageUrl(
              product.image
            );

          const url =
            safeProductUrl(
              product.url
            );

          const sizesMarkup =
            buildSizesMarkup(
              product
            );

          return `
            <article class="ml-compare-product">

              <div class="ml-compare-product__image-wrap">

                ${
                  image
                    ? `
                      <button
                        type="button"
                        class="ml-compare-product__image-button"
                        data-ml-compare-image="${escapeHtml(image)}"
                        data-ml-compare-image-title="${escapeHtml(product.title)}"
                        aria-label="Enlarge ${escapeHtml(product.title)}"
                      >

                        <img
                          src="${escapeHtml(image)}"
                          alt="${escapeHtml(product.title)}"
                          class="ml-compare-product__image"
                          loading="lazy"
                        >

                        <span class="ml-compare-product__zoom">

                          <svg
                            width="17"
                            height="17"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <circle
                              cx="11"
                              cy="11"
                              r="6"
                              stroke="currentColor"
                              stroke-width="1.5"
                            />

                            <path
                              d="M16 16l4 4M11 8v6M8 11h6"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                            />
                          </svg>

                          View Full Image

                        </span>

                      </button>
                    `
                    : `
                      <div class="ml-compare-product__image-placeholder">
                        Image unavailable
                      </div>
                    `
                }

              </div>

              <div class="ml-compare-product__content">

                <div class="ml-compare-product__intro">

                  <h3 class="ml-compare-product__title">
                    ${escapeHtml(product.title)}
                  </h3>

                  <p class="ml-compare-product__price">
                    ${escapeHtml(product.price)}
                  </p>

                </div>

                <div class="ml-compare-product__row">

                  <span class="ml-compare-product__label">
                    Available Sizes
                  </span>

                  <div class="ml-compare-product__sizes">
                    ${sizesMarkup}
                  </div>

                </div>

                <div class="ml-compare-product__actions">

                  <a
                    href="${escapeHtml(url)}"
                    class="ml-compare-product__view"
                  >
                    View Rug
                  </a>

                  <button
                    type="button"
                    class="ml-compare-product__remove"
                    data-ml-compare-remove="${escapeHtml(product.id)}"
                  >
                    Remove
                  </button>

                </div>

              </div>

            </article>
          `;
        })
        .join('');
  }


  /* =========================================================
     OPEN COMPARE
     ========================================================= */

  function openModal() {
    if (
      selectedProducts.length < 2
    ) {
      return;
    }

    previousFocus =
      document.activeElement;

    renderModal();

    overlay.hidden = false;

    requestAnimationFrame(() => {

      overlay.classList.add(
        'is-open'
      );

      document.body.classList.add(
        'ml-compare-open'
      );

      const closeButton =
        overlay.querySelector(
          '[data-ml-compare-close]'
        );

      if (closeButton) {
        closeButton.focus();
      }
    });
  }


  /* =========================================================
     CLOSE COMPARE
     ========================================================= */

  function closeModal() {
    if (
      imageViewer.classList.contains(
        'is-open'
      )
    ) {
      closeImageViewer();
    }

    overlay.classList.remove(
      'is-open'
    );

    document.body.classList.remove(
      'ml-compare-open'
    );

    window.setTimeout(
      () => {
        overlay.hidden = true;
      },
      220
    );

    if (
      previousFocus &&
      typeof previousFocus.focus ===
        'function'
    ) {
      previousFocus.focus();
    }
  }


  /* =========================================================
     FULL IMAGE VIEWER
     ========================================================= */

  function openImageViewer(
    image,
    title
  ) {
    const viewerImage =
      imageViewer.querySelector(
        '[data-ml-image-viewer-image]'
      );

    const viewerTitle =
      imageViewer.querySelector(
        '[data-ml-image-viewer-title]'
      );

    if (!viewerImage) return;

    const safeImage =
      safeImageUrl(image);

    if (!safeImage) return;

    viewerImage.src =
      safeImage;

    viewerImage.alt =
      title || 'Rug image';

    if (viewerTitle) {
      viewerTitle.textContent =
        title || '';
    }

    imageViewer.hidden = false;

    requestAnimationFrame(() => {

      imageViewer.classList.add(
        'is-open'
      );

      const closeButton =
        imageViewer.querySelector(
          '[data-ml-image-viewer-close]'
        );

      if (closeButton) {
        closeButton.focus();
      }
    });
  }

  function closeImageViewer() {
    imageViewer.classList.remove(
      'is-open'
    );

    window.setTimeout(
      () => {

        imageViewer.hidden = true;

        const viewerImage =
          imageViewer.querySelector(
            '[data-ml-image-viewer-image]'
          );

        if (viewerImage) {
          viewerImage.src = '';
        }
      },
      200
    );
  }


  /* =========================================================
     EXPAND / COLLAPSE SIZES
     ========================================================= */

  function toggleMoreSizes(button) {
    const card =
      button.closest(
        '.ml-compare-product'
      );

    if (!card) return;

    const extraSizes =
      card.querySelectorAll(
        '[data-ml-extra-size]'
      );

    const expanded =
      button.getAttribute(
        'aria-expanded'
      ) === 'true';

    extraSizes.forEach(
      (size) => {
        size.hidden =
          expanded;
      }
    );

    button.setAttribute(
      'aria-expanded',
      expanded
        ? 'false'
        : 'true'
    );

    button.classList.toggle(
      'is-expanded',
      !expanded
    );

    button.textContent =
      expanded
        ? `+ ${button.dataset.moreCount} more sizes`
        : 'Show fewer sizes';
  }


  /* =========================================================
     TOAST
     ========================================================= */

  function showToast(message) {
    toast.textContent =
      message;

    toast.classList.add(
      'is-visible'
    );

    window.clearTimeout(
      toastTimer
    );

    toastTimer =
      window.setTimeout(
        () => {

          toast.classList.remove(
            'is-visible'
          );

        },
        2400
      );
  }


  /* =========================================================
     UPDATE EVERYTHING
     ========================================================= */

  function renderAll() {
    syncButtons();
    renderTray();

    if (
      overlay.classList.contains(
        'is-open'
      )
    ) {
      renderModal();
    }
  }


  /* =========================================================
     CLICK HANDLING
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {

      /* SHOW MORE SIZES */

      const sizeMoreButton =
        event.target.closest(
          '[data-ml-size-more]'
        );

      if (sizeMoreButton) {
        event.preventDefault();

        toggleMoreSizes(
          sizeMoreButton
        );

        return;
      }


      /* OPEN FULL IMAGE */

      const imageButton =
        event.target.closest(
          '[data-ml-compare-image]'
        );

      if (imageButton) {
        event.preventDefault();
        event.stopPropagation();

        openImageViewer(
          imageButton.dataset
            .mlCompareImage,

          imageButton.dataset
            .mlCompareImageTitle
        );

        return;
      }


      /* CLOSE FULL IMAGE BUTTON */

      if (
        event.target.closest(
          '[data-ml-image-viewer-close]'
        )
      ) {
        event.preventDefault();

        closeImageViewer();

        return;
      }


      /* CLOSE FULL IMAGE BY BACKDROP */

      if (
        event.target ===
          imageViewer &&
        imageViewer.classList.contains(
          'is-open'
        )
      ) {
        closeImageViewer();

        return;
      }


      /* COMPARE BUTTON */

      const compareButton =
        event.target.closest(
          '[data-ml-compare-button]'
        );

      if (compareButton) {
        event.preventDefault();
        event.stopPropagation();

        toggleProduct(
          compareButton
        );

        return;
      }


      /* REMOVE PRODUCT */

      const removeButton =
        event.target.closest(
          '[data-ml-compare-remove]'
        );

      if (removeButton) {
        event.preventDefault();

        removeProduct(
          removeButton.dataset
            .mlCompareRemove
        );

        return;
      }


      /* CLEAR ALL */

      if (
        event.target.closest(
          '[data-ml-compare-clear]'
        )
      ) {
        event.preventDefault();

        clearProducts();

        return;
      }


      /* OPEN COMPARE */

      if (
        event.target.closest(
          '[data-ml-compare-open]'
        )
      ) {
        event.preventDefault();

        openModal();

        return;
      }


      /* CLOSE COMPARE BUTTON */

      if (
        event.target.closest(
          '[data-ml-compare-close]'
        )
      ) {
        event.preventDefault();

        closeModal();

        return;
      }


      /* CLOSE COMPARE BY BACKDROP */

      if (
        event.target === overlay &&
        overlay.classList.contains(
          'is-open'
        )
      ) {
        closeModal();
      }
    }
  );


  /* =========================================================
     ESCAPE KEY
     ========================================================= */

  document.addEventListener(
    'keydown',
    (event) => {

      if (
        event.key !== 'Escape'
      ) {
        return;
      }

      if (
        imageViewer.classList.contains(
          'is-open'
        )
      ) {
        closeImageViewer();

        return;
      }

      if (
        overlay.classList.contains(
          'is-open'
        )
      ) {
        closeModal();
      }
    }
  );


  /* =========================================================
     INITIALIZE
     ========================================================= */

  function initialize() {
    document.body.appendChild(
      tray
    );

    document.body.appendChild(
      overlay
    );

    document.body.appendChild(
      imageViewer
    );

    document.body.appendChild(
      toast
    );

    renderAll();


    /*
      Shopify filtering and sorting can replace
      product-card HTML without a full page reload.
      This keeps Compare button states synchronized.
    */

    let mutationTimer = null;

    const observer =
      new MutationObserver(
        () => {

          window.clearTimeout(
            mutationTimer
          );

          mutationTimer =
            window.setTimeout(
              () => {
                syncButtons();
              },
              100
            );
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );

  } else {

    initialize();
  }
})();